/**
 * user-stream.ts — SSE-tilkobling per innlogget bruker
 *
 * Åpner en POST /api/iam/user/stream med Bearer-JWT i Authorization-
 * headeren og `Accept: text/event-stream`. Svaret leses som en strøm
 * (ReadableStream) og parses manuelt som SSE.
 *
 * NB: EventSource-API-et er IKKE brukt — det støtter verken POST eller
 * custom headers. Backend-kontrakten (vedtatt 2026-09-22) krever POST +
 * Authorization-header, derfor brukes fetch + manuell SSE-parsing.
 *
 * Event-format (fra backend, levert av bruker 2026-09-22):
 *
 *   : ping
 *
 *   event: chat_message
 *   id: 42
 *   data: {"chat_id":3,"message":{"message_id":42,"tenant_id":1,...}}
 *
 *   : ping
 *
 * - Tomme linjer (`: ping`) er SSE-hjerte-slag — ignoreres.
 * - `event:`-feltet angir event-type (f.eks. `chat_message`).
 * - `data:`-feltet er JSON-strengen som sendes til handler.
 *
 * Designvalg:
 * 1. Singleton-pr-strøm — kun ÉN strøm åpen om gangen per nettleserfane.
 *    Hvis open() kalles to ganger, lukkes den første automatisk.
 * 2. Handlers plugges inn via setUserStreamHandlers — ingen kobling til
 *    React/Toast her. UI-komponent bestemmer hva som skjer med eventene.
 * 3. Automatisk reconnect med eksponentiell backoff ved nettverksbrudd.
 *    Ved 401 (utløpt token) stoppes reconnect — brukeren må logge inn
 *    på nytt.
 */

import { API_URL } from './api';
import { getToken } from './auth';
import { normalizeIds } from './ids';

// === Strøm-tilstand (singleton per nettleserfane) ===
let abortController: AbortController | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let currentToken: string | null = null;
// Backoff: starter på 1s, dobler frem til maks 30s
let reconnectDelayMs = 1000;
const RECONNECT_DELAY_MAX_MS = 30_000;
/** Hvor lenge en strøm må ha vært åpen før den regnes som stabil (og backoff nullstilles) */
const STABLE_CONNECTION_MS = 30_000;

// === Pluggbare listeners ===
// FLERE lyttere kan registreres samtidig (UserStreamListener for Toast,
// JobChat for direkte push). Alle får hvert event — ingen CustomEvent-
// indireksjon via window (den var skøy og vanskelig å debugge).
interface UserStreamHandlers {
  onEvent: (eventType: string, data: unknown) => void;
  onError: (error: unknown) => void;
  onOpen?: () => void;
}

const listeners = new Set<UserStreamHandlers>();

/**
 * Registrerer en lytter som får alle SSE-events. Flere lyttere kan
 * registreres — alle kalles i registreringsrekkefølge.
 */
export function addUserStreamListener(newHandlers: UserStreamHandlers): void {
  listeners.add(newHandlers);
}

/**
 * Fjerner en registrert lytter. Trygt å kalle for ikke-registrerte.
 */
export function removeUserStreamListener(handler: UserStreamHandlers): void {
  listeners.delete(handler);
}

/**
 * Parser én SSE-blokk (adskilt av tom linje) og leverer event til handler.
 *
 * Format per blokk:
 *   event: <type>
 *   id: <id>
 *   data: <json-streng>
 *
 * Linjer som starter med `:` (f.eks. `: ping`) er hjerte-slag — ignoreres.
 */
function dispatchSseBlock(lines: string[]): void {
  let eventType = '';
  let data = '';

  for (const line of lines) {
    if (line.startsWith(':')) continue; // hjerte-slag / kommentar
    if (line.startsWith('event:')) {
      eventType = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      // Flere data:-linjer konkatenères med \n per SSE-spec
      data += (data ? '\n' : '') + line.slice('data:'.length).trimStart();
    }
    // id:-linjen trengs ikke — vi har ingen Last-Event-ID-reconnect
  }

  if (!eventType || !data) return;

  try {
    const parsed = normalizeIds(JSON.parse(data));
    if (process.env.NODE_ENV === 'development') {
      console.info(
        `[user-stream] Event mottatt: ${eventType} (${listeners.size} lyttere)`,
        parsed
      );
    }
    // Lever til ALLE registrerte lyttere
    for (const listener of listeners) {
      try {
        listener.onEvent(eventType, parsed);
      } catch (err) {
        // Én feilende lytter skal ikke stoppe de andre
        console.error('[user-stream] Lytter kastet feil:', err);
      }
    }
  } catch {
    // Ugyldig JSON — ignorer
  }
}

/**
 * Leser response-body som SSE-strøm og dispatcher events.
 *
 * Buffer akkumulerer tekst til en tom linje (\n\n) signaliserer at en
 * komplett SSE-blokk er mottatt.
 */
async function readSseStream(response: Response, signal: AbortSignal, openedAt: number): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE-blokker adskilles med tom linje (\n\n)
      for (;;) {
        const separatorIndex = buffer.indexOf('\n\n');
        if (separatorIndex === -1) break;

        const block = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);

        const lines = block.split('\n').filter((line) => line.length > 0);
        if (lines.length > 0) {
          dispatchSseBlock(lines);
        }
      }
    }
  } finally {
    reader.releaseLock();
    // signal.aborted = bevisst lukking (closeUserStream) — ikke reconnect
    if (!signal.aborted) {
      // Backoff nullstilles bare hvis strømmen faktisk holdt en stund. Lukker serveren strømmen med en gang, ellers ville vi koblet til på nytt hvert sekund for alltid.
      if (Date.now() - openedAt >= STABLE_CONNECTION_MS) reconnectDelayMs = 1000;
      scheduleReconnect();
    }
  }
}

/**
 * Planlegger reconnect med eksponentiell backoff.
 */
function scheduleReconnect(): void {
  if (!currentToken || abortController === null) return; // strømmen er lukket

  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (currentToken) {
      connect(currentToken);
    }
  }, reconnectDelayMs);

  // Dobbel backoff frem til maks
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, RECONNECT_DELAY_MAX_MS);
}

/**
 * Intern: åpner fetch-basert SSE-tilkobling.
 *
 * 1. POST med Authorization: Bearer <token> (backend-kontrakt)
 * 2. Ved 200: reset backoff, les strømmen
 * 3. Ved 401/403: utløpt/ugyldig token — IKKE reconnect (krever ny login)
 * 4. Ved andre feil: reconnect med backoff
 */
async function connect(token: string): Promise<void> {
  const controller = abortController;
  if (!controller) return;

  try {
    const response = await fetch(`${API_URL}/api/iam/user/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      signal: controller.signal,
    });

    if (response.status === 401 || response.status === 403) {
      // Avvist (utløpt token eller manglende tillatelse) — reconnect hjelper ikke. Strømmen lukkes og forsøkes ikke på nytt; brukeren sendes ikke bort herfra.
      for (const listener of listeners) listener.onError(new Error(`SSE: ${response.status} — token avvist`));
      closeUserStream();
      return;
    }

    if (!response.ok || !response.body) {
      // Serverfeil / gateway-problem — prøv igjen med backoff
      for (const listener of listeners) listener.onError(new Error(`SSE: HTTP ${response.status}`));
      scheduleReconnect();
      return;
    }

    // Vellykket tilkobling: lever events (backoff nullstilles først når strømmen har vist seg stabil, se readSseStream)
    if (process.env.NODE_ENV === 'development') {
      console.info('[user-stream] SSE-tilkobling åpnet (POST 200)');
    }
    for (const listener of listeners) listener.onOpen?.();
    await readSseStream(response, controller.signal, Date.now());
  } catch (err) {
    // AbortError = bevisst lukking — ikke reconnect
    if (err instanceof DOMException && err.name === 'AbortError') return;
    // Nettverksfeil — reconnect med backoff
    for (const listener of listeners) listener.onError(err);
    scheduleReconnect();
  }
}

/**
 * Åpner SSE-tilkobling per bruker mot POST /api/iam/user/stream.
 *
 * 1. Guard mot SSR (fetch/ReadableStream brukes kun på klienten)
 * 2. Hvis en strøm allerede er åpen → lukk den først (idempotent)
 * 3. POST med Authorization-header per backend-kontrakt
 *
 * @throws Error hvis token mangler
 */
export function openUserStream(token: string): void {
  // 1. SSR-guard
  if (typeof window === 'undefined') {
    return;
  }

  // 2. Idempotens: lukk eksisterende strøm først
  closeUserStream();

  // 3. Åpne ny strøm
  currentToken = token;
  abortController = new AbortController();
  void connect(token);
}

/**
 * Lukker SSE-tilkoblingen og nullstiller referanser.
 * Trygt å kalle selv om ingen strøm er åpen.
 */
export function closeUserStream(): void {
  // Avbryt pågående fetch + strøm-lesing
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  // Avbryt eventuell ventende reconnect
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  currentToken = null;
  reconnectDelayMs = 1000;
}

/**
 * Sjekker om en strøm er åpen/under oppsett. Brukes av AuthContext for
 * cleanup-sjekk.
 */
export function isUserStreamOpen(): boolean {
  return abortController !== null;
}

/**
 * Hjelpefunksjon: hent token og åpne stream. Brukes av AuthContext etter
 * login. Returnerer true hvis strømmen ble åpnet, false hvis token mangler
 * eller vi er i SSR.
 */
export function openUserStreamFromStorage(): boolean {
  const token = getToken();
  if (!token) return false;
  openUserStream(token);
  return abortController !== null;
}